import { z } from "zod";
import { IsoDateTime, PlayerRef } from "../common.ts";
import {
  ApproximateCountV1,
  OperatorFilter,
  PageParams,
  PeriodBound,
  PeriodV1,
  ReasonFilter,
  SubjectStatusV1,
} from "./common.ts";

// `GET /users`, `GET /devices`, `GET /kpis` (spec explorer-user-list; `explorer.read`). The period
// bounds are UTC days or instants (`PeriodBound`); without them it defaults to the first day of the
// current month up to today in UTC days (the console always sends its user's days as instants).

const listFilters = {
  from: PeriodBound.optional(),
  to: PeriodBound.optional(),
  operator_id: OperatorFilter.optional(),
  reason: ReasonFilter.optional(),
};

export const LIST_SORTS = ["transactions", "first_seen", "last_seen"] as const;

export const UserListQueryV1 = z
  .object({
    ...listFilters,
    /** Identifier search (Enter to apply) with its type selector. */
    q: z.string().trim().min(1).max(128).optional(),
    q_type: z.enum(["player_ref", "device_ref"]).default("player_ref"),
    sort: z.enum(LIST_SORTS).default("transactions"),
    order: z.enum(["asc", "desc"]).default("desc"),
    ...PageParams,
  })
  .strict()
  .meta({ id: "UserListQueryV1" });
export type UserListQueryV1 = z.infer<typeof UserListQueryV1>;

export const DeviceListQueryV1 = z
  .object({
    ...listFilters,
    q: z.string().trim().min(1).max(128).optional(),
    q_type: z.enum(["device_ref", "player_ref"]).default("device_ref"),
    sort: z.enum(LIST_SORTS).default("transactions"),
    order: z.enum(["asc", "desc"]).default("desc"),
    ...PageParams,
  })
  .strict()
  .meta({ id: "DeviceListQueryV1" });
export type DeviceListQueryV1 = z.infer<typeof DeviceListQueryV1>;

/** Related subjects column: the identifier when there is one, otherwise the count (expandable). */
const Related = (ref: z.ZodType) =>
  z.object({ count: z.int().nonnegative(), single: ref.optional() }).strict();

export const UserRowV1 = z
  .object({
    operator_id: z.uuid(),
    player_ref: PlayerRef,
    status: SubjectStatusV1,
    /** Transactions in the period. */
    transactions: z.int().nonnegative(),
    first_seen_at: IsoDateTime,
    last_seen_at: IsoDateTime,
    /** Devices used in the period. */
    devices: Related(z.uuid()),
  })
  .strict()
  .meta({ id: "UserRowV1" });

export const DeviceRowV1 = z
  .object({
    operator_id: z.uuid(),
    device_ref: z.uuid(),
    status: SubjectStatusV1,
    transactions: z.int().nonnegative(),
    first_seen_at: IsoDateTime,
    last_seen_at: IsoDateTime,
    os: z.string(),
    browser: z.string(),
    browser_version: z.string(),
    /** Players that used the device in the period. */
    users: Related(PlayerRef),
  })
  .strict()
  .meta({ id: "DeviceRowV1" });

const page = {
  period: PeriodV1,
  page: z.int().min(1),
  size: z.int().min(1),
  /** Approximate total of the active tab in the period ("~42 Users"). */
  total: ApproximateCountV1,
};

export const UserListResponseV1 = z
  .object({ ...page, items: z.array(UserRowV1) })
  .strict()
  .meta({ id: "UserListResponseV1" });
export type UserListResponseV1 = z.infer<typeof UserListResponseV1>;

export const DeviceListResponseV1 = z
  .object({ ...page, items: z.array(DeviceRowV1) })
  .strict()
  .meta({ id: "DeviceListResponseV1" });
export type DeviceListResponseV1 = z.infer<typeof DeviceListResponseV1>;

export const KpisQueryV1 = z.object(listFilters).strict().meta({ id: "KpisQueryV1" });
export type KpisQueryV1 = z.infer<typeof KpisQueryV1>;

/** Period indicators: new users, total transactions and failure rate (`denied / transactions`). */
export const KpisResponseV1 = z
  .object({
    period: PeriodV1,
    new_users: z.int().nonnegative(),
    transactions: z.int().nonnegative(),
    denied: z.int().nonnegative(),
    failure_rate: z.number().min(0).max(1),
  })
  .strict()
  .meta({ id: "KpisResponseV1" });
export type KpisResponseV1 = z.infer<typeof KpisResponseV1>;
